import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Row, Col } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './tasklist.css';
import '../users/userlist.css';
import './performanceDashboard.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import {
  ScoreCircle,
  ScoreLevelBadge,
  getCorporateTopScores,
  OVERVIEW_PERIOD_OPTIONS,
  DEFAULT_OVERVIEW_PERIOD_KEY,
  scoreColorClass,
  getMeRowClassName,
  PerformanceBadgePills,
} from './performanceShared';
import {
  getMyPeriodScores,
  getTeamPeriodScores,
  getMyBadges,
} from '../../services/PerformanceService';
import PerformanceTaskAnalytics from './performanceTaskAnalytics';
import CommonTable from '../components/CommonTable/CommonTable';

const PerformanceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [periodKey, setPeriodKey] = useState(DEFAULT_OVERVIEW_PERIOD_KEY);
  const [myPeriodScores, setMyPeriodScores] = useState(null);
  const [teamPeriodScores, setTeamPeriodScores] = useState(null);
  const [myBadges, setMyBadges] = useState([]);

  const userData = useMemo(
    () => JSON.parse(localStorage.getItem('user') || '{}'),
    []
  );
  const currentUserId = userData.id ?? userData.userId ?? userData.corporateUserId;

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, teamRes, badgesRes] = await Promise.all([
        getMyPeriodScores(),
        getTeamPeriodScores(),
        getMyBadges(),
      ]);
      setMyPeriodScores(myRes.data?.data ?? null);
      setTeamPeriodScores(teamRes.data?.data ?? null);
      setMyBadges(badgesRes.data?.data?.earned ?? []);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to load performance overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const periodMeta = myPeriodScores?.periods?.[periodKey];
  const myScore = myPeriodScores?.scores?.[periodKey];
  const periodLabel =
    periodMeta?.label ||
    OVERVIEW_PERIOD_OPTIONS.find((o) => o.key === periodKey)?.shortLabel ||
    'Selected period';
  const topFive = useMemo(
    () => getCorporateTopScores(teamPeriodScores, periodKey, 5),
    [teamPeriodScores, periodKey]
  );
  const fullRanking = useMemo(
    () => getCorporateTopScores(teamPeriodScores, periodKey, 9999),
    [teamPeriodScores, periodKey]
  );
  const myRank = useMemo(() => {
    const idx = fullRanking.findIndex(
      (row) => String(row.user_id) === String(currentUserId)
    );
    return idx >= 0 ? idx + 1 : null;
  }, [fullRanking, currentUserId]);
  const myInTopFive = myRank != null && myRank <= 5;

  const topFiveColumns = useMemo(
    () => [
      {
        key: 'rank',
        header: '#',
        headerStyle: { width: '12%' },
        cellStyle: { width: '12%' },
        cellClassName: 'performance-top-five-rank',
        accessor: (row, index) => index + 1,
      },
      {
        key: 'name',
        header: 'Name',
        headerStyle: { width: '58%' },
        cellStyle: { width: '58%' },
        render: (row) => (
          <div className="performance-top-five-name-cell">
            <div className="fw-semibold text-truncate" title={row.full_name}>
              {row.full_name}
            </div>
            {row.department ? (
              <small className="text-muted d-block text-truncate" title={row.department}>
                {row.department}
              </small>
            ) : null}
          </div>
        ),
      },
      {
        key: 'score',
        header: 'Score',
        headerStyle: { width: '30%', textAlign: 'right' },
        cellStyle: { width: '30%', textAlign: 'right' },
        cellClassName: 'performance-score-cell',
        render: (row) => (
          <span
            className={`performance-score-badge ${scoreColorClass(row.color?.color)}`}
          >
            {row.performance_score}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <Row className="performance-overview-page performance-compact performance-theme" style={{ marginBottom: '0px !important' }}>
      <ToastContainer position="top-right" autoClose={4000} />

      <Col lg={12}>
        <Card className="user-list-card performance-overview-card">
          <Card.Header className="performance-card-header">
            <div className="performance-header-row performance-header-row--actions-only">
              <div className="performance-period-toggle">
                {OVERVIEW_PERIOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`performance-period-btn${periodKey === opt.key ? ' active' : ''}`}
                    onClick={() => setPeriodKey(opt.key)}
                    disabled={loading}
                  >
                    {opt.shortLabel}
                  </button>
                ))}
              </div>
              <Link to="/task/performance/details" className="new-user-btn performance-details-link">
                <i className="fas fa-chart-bar me-1" aria-hidden />
                Details
              </Link>
            </div>
          </Card.Header>

          <Card.Body className="performance-card-body">
            {loading ? (
              <div className="loading-container performance-loading-compact">
                <div className="loading-spinner" />
              </div>
            ) : (
              <>
              <div className="performance-overview-meta-row mb-2">
                <p className="performance-period-context mb-0">
                  <i className="fas fa-calendar-alt me-1" aria-hidden />
                  <span>{periodLabel}</span>
                  {myPeriodScores?.computed_through ? (
                    <span className="performance-period-context__through">
                      · through {myPeriodScores.computed_through}
                    </span>
                  ) : null}
                </p>
                <div className="performance-overview-my-badges-bar">
                  <span className="performance-overview-my-badges-label">Your badges</span>
                  <PerformanceBadgePills
                    badges={myBadges}
                    className="performance-top-five-badges"
                    emptyPlaceholder={
                      <span className="text-muted small">No badges earned yet.</span>
                    }
                  />
                </div>
              </div>
              <Row className="g-2 align-items-stretch performance-overview-row">
                <Col lg={3} md={4}>
                  <div className="performance-my-score-panel h-100">
                    <div className="performance-my-score-main">
                      <div
                        className={`period-score-label ${
                          myScore?.color?.color
                            ? scoreColorClass(myScore.color.color)
                            : 'score-grey'
                        }`}
                      >
                        Your score
                      </div>
                      {periodMeta?.empty ? (
                        <p className="text-muted small mb-0 mt-1">
                          {periodMeta.empty_reason || 'Not available for this period.'}
                        </p>
                      ) : (
                        <>
                          <ScoreCircle
                            score={myScore?.performance_score}
                            color={myScore?.color}
                            size="sm"
                          />
                          <ScoreLevelBadge color={myScore?.color} />
                          {myRank != null ? (
                            <p className="text-muted small mb-0 mt-1 performance-rank-line">
                              {myInTopFive
                                ? `#${myRank} in top 5`
                                : `Rank #${myRank} of ${fullRanking.length}`}
                            </p>
                          ) : myScore?.performance_score != null ? (
                            <p className="text-muted small mb-0 mt-1">Rank N/A</p>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                </Col>

                <Col lg={9} md={8}>
                  <h6 className="mb-2 performance-top-five-heading">Corporate top 5</h6>
                  <div className="row">
                    <CommonTable
                      className="performance-table-fit performance-table-fit--short"
                      columns={topFiveColumns}
                      data={topFive}
                      emptyMessage="No scores for this period yet."
                      emptyStateIcon="fas fa-trophy"
                      getRowId={(row) => row.user_id}
                      getRowClassName={(row) =>
                        getMeRowClassName(row, currentUserId, row.color?.color)
                      }
                      maxHeight="none"
                      minHeight="0"
                    />
                  </div>
                </Col>
              </Row>
              </>
            )}

            <PerformanceTaskAnalytics />
          </Card.Body>
        </Card>
      </Col>

      <InternalFooter />
    </Row>
  );
};

export default PerformanceDashboard;
